import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import { firePixels } from "../lib/pixels";
import LinkOverlay from "../components/LinkOverlay";

const RESERVED_PATHS = new Set([
  "login",
  "signup",
  "forgot",
  "onboarding",
  "dashboard",
  "links",
  "analytics",
  "settings",
  "pricing",
  "payment-success",
  "reset-password",
  "domains",
  "campaigns",
  "unlock",
  "blocked",
  "challenge",
  "bulk",
  "api-keys",
  "webhooks",
  "team",
  "integrations",
  "docs",
  "billing",
  "api",
  "bio",
  "s",
  "blog",
  "guides",
  "compare",
  "alternatives",
  "use-cases",
  "privacy",
  "terms",
  "security",
  "subprocessors",
  "what-is-linklane",
  "about",
  "contact",
  "customers",
  "careers",
  "developers",
  "status",
]);

function isBareCode(code) {
  if (!code) return false;
  if (RESERVED_PATHS.has(code.toLowerCase())) return false;
  return /^[A-Za-z0-9_-]{3,32}$/.test(code);
}

export default function Redirector() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("resolving");
  // When a link carries a CTA overlay we pause here and show the banner instead
  // of redirecting straight away. { overlay, dest }
  const [overlay, setOverlay] = useState(null);
  // StrictMode double-invokes effects in dev; guard against double-click logging.
  const ranRef = useRef(false);

  const go = useCallback((url) => {
    window.location.replace(url);
  }, []);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!code) {
      setStatus("not_found");
      return;
    }
    if (!isBareCode(code)) {
      setStatus("not_found");
      return;
    }
    (async () => {
      const host = window.location.hostname;
      try {
        const res = await fetch(
          `${API_BASE}/preview/${encodeURIComponent(code)}?host=${encodeURIComponent(host)}`
        );
        if (!res.ok) {
          if (res.status === 410 || res.status === 404) {
            navigate(`/unlock/${encodeURIComponent(code)}`, { replace: true });
            return;
          }
          setStatus("not_found");
          return;
        }
        const data = await res.json();
        if (data.access_status === "geo_blocked") {
          navigate(`/blocked/${encodeURIComponent(code)}`, { replace: true });
          return;
        }
        if (data.access_status === "bot_challenge") {
          navigate(`/challenge/${encodeURIComponent(code)}`, { replace: true });
          return;
        }
        if (data.access_status && data.access_status !== "ok") {
          navigate(`/unlock/${encodeURIComponent(code)}`, { replace: true });
          return;
        }
        if (!data.url) {
          navigate(`/unlock/${encodeURIComponent(code)}`, { replace: true });
          return;
        }
        try {
          const src = new URLSearchParams(window.location.search).get("src");
          await fetch(`${API_BASE}/links/${encodeURIComponent(code)}/click`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              source: src === "qr" ? "qr" : "link",
              referrer: document.referrer || null,
              host,
            }),
            keepalive: true,
          });
        } catch {
          /* ignore */
        }
        if (!data.url.startsWith("https://") && !data.url.startsWith("http://")) {
          setStatus("not_found");
          return;
        }
        // Retargeting pixels normally fire on the server /r/ interstitial, which
        // this SPA path skips — so fire them here, in-browser, before leaving.
        if (data.pixels) {
          try {
            firePixels(data.pixels);
          } catch {
            /* never block the redirect on a pixel error */
          }
        }
        // CTA overlay → show the branded banner and let the visitor continue.
        if (data.overlay) {
          setOverlay({ overlay: data.overlay, dest: data.url });
          setStatus("overlay");
          return;
        }
        // Pixels but no overlay → give them a beat to fire, then redirect.
        if (data.pixels) {
          setTimeout(() => go(data.url), 200);
          return;
        }
        go(data.url);
      } catch (err) {
        toast.error("Failed to resolve.");
        setStatus("not_found");
      }
    })();
  }, [code, navigate, go]);

  if (status === "overlay" && overlay) {
    return (
      <LinkOverlay
        overlay={overlay.overlay}
        destination={overlay.dest}
        onContinue={() => go(overlay.dest)}
      />
    );
  }

  if (status === "resolving") {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-neutral-50"
        data-testid="redirector-page"
      >
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-700"
            aria-hidden
          />
          <span className="text-sm text-neutral-500">Redirecting…</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-neutral-50 p-4"
      data-testid="redirector-page"
    >
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-lg md:p-10">
        <h1 className="text-xl font-semibold text-neutral-900 md:text-2xl">Link not found</h1>
        <p className="mt-2 text-sm text-neutral-500">
          This link doesn’t exist or has been removed.
        </p>
      </div>
    </div>
  );
}

export { isBareCode };
